import React, { useState } from 'react';
import i18next from 'core/i18n';
import { useMutation } from '@apollo/client';

import { FinancingUser } from '@indigo-ag/schema';
import { DialogContent, DialogActions, Button, useFlashMessage, DialogProps } from '@indigo-ag/component-module';

import { EnhancedDialog } from 'shared';
import { financingUsers, deleteFinancingUser as deleteFinancingUserMut } from 'features/admin/adminQueries';

interface DeleteUserDialogProps extends DialogProps {
  userToDelete: FinancingUser | null;
  closeDialog: () => void;
}

export const DeleteUserDialog: React.FunctionComponent<DeleteUserDialogProps> = ({
  userToDelete,
  open,
  closeDialog
}) => {
  /**
   * State
   */
  const [isDeletingUser, setIsDeletingUser] = useState(false);

  /**
   * Mutations
   */
  const [deleteFinancingUserMutFn] = useMutation(deleteFinancingUserMut);

  const { addFlashMessage } = useFlashMessage();

  const removeFinancingAdmin = () => {
    if (!userToDelete) {
      return;
    }

    setIsDeletingUser(true);
    const { id } = userToDelete;

    deleteFinancingUserMutFn({
      awaitRefetchQueries: true,
      refetchQueries: [{ query: financingUsers }],
      variables: {
        id
      }
    })
      .then(() => {
        closeDialog();
      })
      .catch(() => {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      })
      .finally(() => setIsDeletingUser(false));
  };
  return (
    <EnhancedDialog
      open={open}
      onClose={closeDialog}
      title={i18next.t('confirmation')}
      dialogMinHeight="0"
      dialogMaxHeight="15rem"
      dialogMaxWidth="22rem"
    >
      <DialogContent>
        <div style={{ textAlign: 'center' }}>{i18next.t('deleteUserConfirm')}</div>
        <small style={{ textAlign: 'center', marginTop: '.5rem', display: 'block', fontWeight: 500 }}>
          {userToDelete?.email}
        </small>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} type="button" size="small" variant="secondary">
          {i18next.t('cancel')}
        </Button>
        <Button onClick={removeFinancingAdmin} type="submit" size="small" busy={isDeletingUser}>
          {i18next.t('continue')}
        </Button>
      </DialogActions>
    </EnhancedDialog>
  );
};
